import React, { FC, useCallback, useEffect, useState } from 'react';

import { Button, Flex, IconButton, Select, Stack } from '@chakra-ui/react';
import { range } from 'lodash';
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from 'react-icons/fi';
import { usePrevious } from 'react-use';

import { paginationStyles } from './styles';
import { DefaultPageSizes, IPagination } from './types';
import { getItemRange } from './utils';

export const Pagination: FC<IPagination> = props => {
  const {
    totalCount = 0,
    onChange,
    pageInfo,
    styles,
    cursorBased = false,
    exactTotal = false,
  } = props;
  const [pageSizes] = useState(props.pageSizes || DefaultPageSizes);

  if (pageInfo.page_number < 1) {
    throw Error(
      `minimum pageNumber is 1, but the provided value is less than 1`,
    );
  }
  if (pageInfo.page_size < 1) {
    throw Error(`minimum pageSize is 1, but the provided value is less than 1`);
  }

  const [currentPage, setCurrentPage] = useState(pageInfo.page_number);
  const [itemsPerPage, setItemsPerPage] = useState(pageInfo.page_size);
  const prevPageSize = usePrevious(itemsPerPage);
  // const prevPageSizes = usePrevious(pageSizes);
  const prevCurrentPage = usePrevious(currentPage);
  const prevItemsPerPage = usePrevious(itemsPerPage);

  useEffect(() => {
    if (pageInfo.page_number !== prevCurrentPage) {
      setCurrentPage(pageInfo.page_number);
    }
  }, [pageInfo.page_number, prevCurrentPage]);

  useEffect(() => {
    if (pageInfo.page_size !== prevPageSize) {
      setItemsPerPage(pageInfo.page_size);
    }
  }, [pageInfo.page_size, prevPageSize]);

  // maxPage >= 1
  const [maxPage, setMaxPage] = useState(1);

  useEffect(() => {
    if (cursorBased) {
      const hasNextPage = totalCount > currentPage * itemsPerPage;
      setMaxPage(currentPage + (hasNextPage ? 1 : 0));
      return;
    }
    setMaxPage(Math.max(Math.ceil(totalCount / itemsPerPage), 1));
  }, [cursorBased, currentPage, itemsPerPage, totalCount]);

  // The shown range is derived from the current page. It used to be an effect
  // that treated the first render as a page-size change and jumped to page 1.
  const { startItemNumber, endItemNumber } = getItemRange(
    currentPage,
    itemsPerPage,
    totalCount,
  );

  function next() {
    setCurrentPage((currentPage: number) => Math.min(currentPage + 1, maxPage));
  }

  function prev() {
    setCurrentPage((currentPage: number) => Math.max(currentPage - 1, 1));
  }

  function jump(page: number) {
    const pageNumber = Math.min(Math.max(1, page), maxPage);
    setCurrentPage(pageNumber);
  }

  const handleUpdate = useCallback(
    (pageNumber, itemsPerPage) => {
      onChange({ page_number: pageNumber, page_size: itemsPerPage });
    },
    [onChange],
  );

  const handlePageChange = useCallback(
    pageNumber => handleUpdate(pageNumber, itemsPerPage),
    [handleUpdate, itemsPerPage],
  );

  useEffect(() => {
    if (prevCurrentPage !== currentPage || itemsPerPage !== prevItemsPerPage) {
      if (
        currentPage !== pageInfo.page_number ||
        itemsPerPage !== pageInfo.page_size
      ) {
        handleUpdate(currentPage, itemsPerPage);
      }
    }
  }, [
    currentPage,
    handleUpdate,
    itemsPerPage,
    pageInfo,
    prevCurrentPage,
    prevItemsPerPage,
  ]);

  // Reset to page 1 in the same update as the new size, so only one valid
  // page (1 at the new size) is requested instead of the old page first.
  const handleItemsPerPageChange = (e: any) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  if (props.isLoading || totalCount === 0) {
    return <></>;
  }

  const hasNextCursorPage =
    cursorBased && totalCount > currentPage * itemsPerPage;
  // Cursor APIs that report an exact total show it; others show "N+".
  const displayedTotal =
    cursorBased && !exactTotal
      ? `${endItemNumber}${hasNextCursorPage ? '+' : ''}`
      : totalCount;

  const renderPageNumberButtons = (startNumber, endNumber) => {
    return range(startNumber, endNumber + 1).map(number => (
      <Button
        bg="transparent"
        size={'sm'}
        onClick={() => handlePageChange(number)}
        key={number}
        fontWeight={number === currentPage ? 'bold' : 'normal'}
      >
        {number}
      </Button>
    ));
  };

  const renderPageNumbers = () => {
    // if (maxPage <= 5) {
    return (
      <Stack isInline spacing={1}>
        {renderPageNumberButtons(currentPage, currentPage)}
      </Stack>
    );
    // }
  };

  return (
    <Flex {...paginationStyles} right={0} {...styles}>
      {/*<Flex>{text ? text : 'Rows per page'} &nbsp;</Flex>*/}

      <Stack isInline spacing={'1px'} alignItems={'center'}>
        {!cursorBased && (
          <IconButton
            bg="transparent"
            size={'sm'}
            aria-label="First page"
            icon={<FiChevronsLeft />}
            onClick={() => jump(0)}
          />
        )}
        <IconButton
          bg="transparent"
          size={'sm'}
          aria-label="Prev page"
          icon={<FiChevronLeft />}
          onClick={() => prev()}
        />
        <Flex mx={2}>{renderPageNumbers()}</Flex>
        <IconButton
          bg="transparent"
          size={'sm'}
          aria-label="Next page"
          icon={<FiChevronRight />}
          onClick={() => next()}
        />
        {!cursorBased && (
          <IconButton
            bg="transparent"
            size={'sm'}
            aria-label="Last page"
            icon={<FiChevronsRight />}
            onClick={() => jump(maxPage)}
          />
        )}
      </Stack>
      <Flex ml={4} w={70}>
        <Select
          value={itemsPerPage}
          size={'sm'}
          onChange={handleItemsPerPageChange}
          borderRadius={'md'}
        >
          {pageSizes.map(v => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </Select>
      </Flex>
      <Flex mx={4}>
        {startItemNumber}-{endItemNumber} of {displayedTotal}
      </Flex>
    </Flex>
  );
};
